import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Loader from '../../../../Components/Utility/Loader/Loader';
import { BombiixModal } from '../../../../Components/BombiixModal';
import { NewDatePicker } from '../../../../Components/PIM/common/NewDatePicker';
import './sample-tracking-dates.scss';
import { unMOunt, getSampleTraking } from '../../../DevlopmentOrder/store/DevelopmentOrder.action';
import TrakingDates from './TrakingDates/TrakingDates';
import { checkArray } from '../../../../Components/helper';
import { BulkDatesModalProps, MeasurementsSlice, TrackingDateField } from './store/Samples.type';

/**
 * Sample Tracking Dates — bulk edit modal. Shares the chrome (BombiixModal) and the
 * tracking-dates card with the per-sample modal; the difference is that rows are seeded
 * from the tracking-date masters alone (there is no single sample to read values from),
 * and Save is rejected until at least one date has been entered.
 */
function BulkDatesModal({ onSubmitForm, show, toggle, productBrand }: BulkDatesModalProps) {
  const { sampleTrakingDates } = useSelector((redState: MeasurementsSlice) => redState.Measuremets);
  const [showLoader, setShowLoader] = useState(false);
  const [showError, setShowError] = useState(false);
  /* react-i18next@11.8 types t() as returning TFunctionResult, which @types/react@19 rejects
     as a ReactNode. TranslateFn is the shared cast declared in src/global.d.ts. */
  const { t: translate } = useTranslation('common');
  const t = translate as unknown as TranslateFn;
  const dispatch = useDispatch();

  const { control, handleSubmit } = useForm({ mode: 'onBlur' });

  const {
    fields: trackingDatesFields,
    remove: trackingDatesRemove,
    append: trackingDatesAppend,
    update: trackingDatesUpdate,
  } = useFieldArray({ control, name: 'trackingDates' });

  useEffect(() => {
    return () => {
      dispatch(unMOunt());
    };
  }, [dispatch]);

  useEffect(() => {
    if (checkArray(sampleTrakingDates) && trackingDatesFields.length === 0) {
      let trackingDatesToUse: TrackingDateField[] = sampleTrakingDates ?? [];
      const selectedBrandId = productBrand?.[0]?._id;
      if (productBrand && productBrand.length > 0 && selectedBrandId) {
        trackingDatesToUse = trackingDatesToUse.filter(
          (trackingDate: TrackingDateField) => Array.isArray(trackingDate.brands) && trackingDate.brands.includes(selectedBrandId),
        );
      }
      trackingDatesAppend([...trackingDatesToUse.filter((val: TrackingDateField) => val.isActive)]);
    }
  }, [sampleTrakingDates]);

  const onSubmitMethod = async (data: Record<string, unknown>) => {
    const trackingDatesArray = (data.trackingDates as TrackingDateField[]) ?? [];
    const hasAnyDate = Boolean(data.dueDate) || trackingDatesArray.some((item: TrackingDateField) => Boolean(item.value));

    if (!hasAnyDate) {
      await dispatch(getSampleTraking());
      setShowError(true);
      setShowLoader(false);
      return;
    }

    setShowLoader(true);
    onSubmitForm({ ...data, trackingDates: trackingDatesArray });
    setShowLoader(false);
  };

  return (
    <BombiixModal
      show={show}
      title={t('TAKING_DATES.SAMPLE_TRACKING_DATE')}
      onClose={toggle}
      onSave={handleSubmit(onSubmitMethod)}
      saveDisabled={showLoader}
      size="md"
    >
      {showLoader ? (
        <Loader />
      ) : (
        <form>
          <div className="sample-tracking-field bombiix-input-wrap mb-3">
            <span className="bombiix-input__label">{t('TAKING_DATES.DUE_DATE')}</span>
            <Controller
              name="dueDate"
              control={control}
              render={({ field }) => (
                <NewDatePicker
                  dateFormat="dd/MM/yyyy"
                  placeholder={t('TAKING_DATES.DUE_DATE')}
                  popperPlacement="bottom-start"
                  value={(field.value as Date | null) ?? null}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          <p className="bombiix-input__label">{t('TAKING_DATES.SAMPLE_TRACKING_DATE')}</p>
          <TrakingDates
            trackingDatesFields={trackingDatesFields as unknown as TrackingDateField[]}
            trackingDatesRemove={trackingDatesRemove}
            trackingDatesUpdate={trackingDatesUpdate as unknown as (index: number, value: TrackingDateField) => void}
          />

          {showError && <small className="error-message">{t('TAKING_DATES.PLEASE_SELECT')}</small>}
        </form>
      )}
    </BombiixModal>
  );
}

export default BulkDatesModal;
